/**
 * Auth-Service – Anmelden, Registrieren, Session initialisieren.
 *
 * CSRF-Token-Handling:
 *   Backend setzt nt_csrf als httpOnly:false Cookie beim Login.
 *   readCsrfCookie() liest ihn aus und speichert ihn in gameState.
 *   api.ts sendet ihn als X-CSRF-Token Header bei allen state-ändernden Requests.
 */

import { api, ApiError } from './api.js';
import { gameState, type PlayerSnapshot } from '../state/gameState.js';

// Cookie-Name muss mit dem Backend (auth.routes.ts CSRF_COOKIE_NAME) übereinstimmen
const CSRF_COOKIE_NAME = 'nt_csrf';

/** CSRF-Token aus dem Browser-Cookie lesen (httpOnly: false – JS-Zugriff erlaubt). */
function readCsrfCookie(): string | null {
  const entry = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${CSRF_COOKIE_NAME}=`));
  if (!entry) return null;
  return entry.split('=')[1] ?? null;
}

export interface LoginResult {
  username: string;
  // NT-ALPHA-REWORK-01: WAITLIST → Spieler wird auf die Warteliste-Seite geleitet
  accessStatus: 'ACTIVE' | 'WAITLIST';
}

export interface RegisterResult {
  message: string;
}

// Pflicht-Bestätigungen der Regel-Seite (NT-ALPHA-REWORK-01) – alle müssen true sein
export interface RegisterConfirmations {
  rulesAccepted: true;
  privacyAccepted: true;
  contentRestrictionAccepted: true;
  resetNoticeAccepted: true;
  ageConfirmed: true;
}

/**
 * Einloggen: API-Call → CSRF aus Cookie → Spielerprofil laden → gameState aktualisieren.
 * Wirft ApiError bei ungültigen Credentials, Rate-Limit, gesperrtem Account etc.
 */
export async function loginUser(email: string, password: string): Promise<LoginResult> {
  const result = await api.post<LoginResult>('/auth/login', { email, password });

  // CSRF-Token aus Cookie übernehmen (Backend setzt ihn gleichzeitig mit dem Session-Cookie)
  const csrfToken = readCsrfCookie();
  if (csrfToken) gameState.setCsrfToken(csrfToken);

  // Spielerprofil laden (nötig für Auth-Guard und alle geschützten Seiten)
  const player = await api.get<PlayerSnapshot>('/auth/me');
  gameState.setPlayer(player);

  return result;
}

/**
 * Registrieren: API-Call.
 * Passwort-Bestätigung wird nur client-seitig geprüft – nicht an die API geschickt.
 * confirmations: die 5 Pflicht-Bestätigungen der Regel-Seite (alle true).
 * inviteToken: optional, bei REGISTRATION_MODE=closed_alpha vom Backend verlangt (dormant).
 */
export async function registerUser(
  email: string,
  username: string,
  password: string,
  confirmations: RegisterConfirmations,
  inviteToken?: string,
): Promise<RegisterResult> {
  return api.post<RegisterResult>('/auth/register', {
    email,
    username,
    password,
    ...confirmations,
    ...(inviteToken !== undefined ? { inviteToken } : {}),
  });
}

/**
 * Verifikations-Mail erneut anfordern (NT-ALPHA-REWORK-01).
 * Antwort ist immer neutral (kein User-Enumeration). Wirft nur bei Rate-Limit (429).
 */
export async function resendVerification(email: string): Promise<RegisterResult> {
  return api.post<RegisterResult>('/auth/resend-verification', { email });
}

/**
 * Session beim App-Start initialisieren.
 * Wenn ein nt_csrf Cookie vorhanden ist → bestehende Session wiederherstellen.
 * Muss VOR initRouter() aufgerufen werden, damit der Auth-Guard korrekt funktioniert.
 */
export async function initSession(): Promise<void> {
  const csrfToken = readCsrfCookie();
  if (!csrfToken) return; // Kein Cookie → keine aktive Session

  gameState.setCsrfToken(csrfToken);

  try {
    // Direktes fetch statt api.get() – kein automatischer Redirect auf /login bei 401.
    // Auf öffentlichen Seiten (z.B. /alpha) darf eine abgelaufene Session nicht weiterleiten.
    const response = await fetch('/api/v1/auth/me', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      // Session abgelaufen oder ungültig → gameState leeren, aber kein Redirect
      gameState.clearPlayer();
      return;
    }

    const body = (await response.json()) as { data: PlayerSnapshot };
    gameState.setPlayer(body.data);
  } catch {
    // Netzwerkfehler → gameState leeren, kein Redirect
    gameState.clearPlayer();
  }
}

/**
 * Abmelden: Session-Cookie + CSRF-Cookie löschen.
 * Wirft niemals – Logout soll immer durchgehen (Session kann bereits abgelaufen sein).
 */
export async function logoutUser(): Promise<void> {
  try {
    await api.post<{ message: string }>('/auth/logout', {});
  } catch (err) {
    // 401: Session bereits abgelaufen – OK, Logout trotzdem durchführen
    if (!(err instanceof ApiError && err.status === 401)) {
      // Andere Fehler ignorieren – Client-State trotzdem bereinigen
    }
  }
  gameState.clearPlayer();
}
